import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../services/supabaseClient.ts';
import type { ScanFinding, ScanData, CategoryScores, GroupedFindings } from '../types/scan.ts';
import { getScanResults, getDemoScanResults } from '../services/apiService.ts';

export interface ScanProgress {
  stage: 'initializing' | 'scanning' | 'analyzing' | 'completed';
  progress: number; // 0-100
  currentScanner?: string;
  completedScanners: string[];
  totalScanners: number;
  message: string;
}

// Scanner category mapping based on the backend scanner registry
const SCANNER_CATEGORY_MAP: Record<string, 'security' | 'privacy' | 'compliance' | 'miscellaneous'> = {
  // Legacy scanners (from demo/old system)
  'secrets': 'security',
  'hardcoded_credentials': 'security',
  'insecure_defaults': 'security',
  'sdk_exposure': 'security',
  'unrestricted_api': 'security',
  'rbac_misconfig': 'security',
  
  // Privacy scanners
  'privacy_log': 'privacy',
  'public_bucket': 'privacy',
  'supabase_cors': 'privacy',
  
  // Compliance scanners
  'rls_validator': 'compliance',
  'netlify_headers': 'compliance',
  'legal_presence': 'compliance',
  
  // Miscellaneous
  'spa_redirect': 'miscellaneous',
  
  // Real scanner modules (based on actual database data)
  'ai_security_analysis': 'security',
  'ai_privacy_analysis': 'privacy', 
  'ai_legal_compliance': 'compliance',
  'regex_api_keys': 'security',
  'regex_credentials': 'security',
  'regex_privacy': 'privacy',
  'regex_secrets': 'security',
  'hardcoded_password_scanner': 'security',
  'api_key_scanner': 'security',
  'pii_scanner': 'privacy',
  'compliance_checker': 'compliance',
  
  // Advanced Security Scanners - Phase 2 Implementation
  'api_security_comprehensive': 'security',
  'client_side_security': 'security',
  'dependency_security_deep': 'security',
  'framework_security_analyzer': 'security',
  'environment_config_security': 'security',
  'database_security_deep': 'security',
  
  // Edge-friendly scanners (E-1 to E-5)
  'header_tls_auditor': 'security',
  'semgrep_lite': 'security',
  'package_cve_checker': 'security',
  'cookie_storage_linter': 'security',
  'ct_log_enumerator': 'security'
};

// Demo scanner simulation for progress tracking
const DEMO_SCANNERS = [
  { id: 'secrets', name: 'API Key Scanner', duration: 300 },
  { id: 'hardcoded_credentials', name: 'Credential Scanner', duration: 200 },
  { id: 'privacy_log', name: 'Privacy Scanner', duration: 250 },
  { id: 'rls_validator', name: 'Database Security', duration: 180 },
  { id: 'netlify_headers', name: 'Security Headers', duration: 150 },
  { id: 'legal_presence', name: 'Legal Compliance', duration: 120 },
];

// Demo findings that cover all categories
const createDemoFindings = (): ScanFinding[] => [
  // Security findings
  {
    id: 'demo-1',
    scan_id: 'demo-scan',
    module_id: 'secrets',
    file_path: 'src/config.js',
    line: 12,
    issue_type: 'api_key_exposure',
    severity: 'high',
    message: 'OpenAI API key found in configuration file',
    recommendation: 'Move API keys to environment variables',
    is_premium: false,
    description: 'Detects exposed API keys and tokens',
    user_value: 'Prevents unauthorized access to your services',
    engine: 'regex',
    type: 'security',
    created_at: new Date().toISOString()
  },
  {
    id: 'demo-2',
    scan_id: 'demo-scan',
    module_id: 'hardcoded_credentials',
    file_path: 'src/auth.js',
    line: 8,
    issue_type: 'hardcoded_password',
    severity: 'medium',
    message: 'Hardcoded password found in authentication module',
    recommendation: 'Use secure credential management',
    is_premium: false,
    description: 'Detects hardcoded passwords and secrets',
    user_value: 'Prevents credential leaks in source code',
    engine: 'regex',
    type: 'security',
    created_at: new Date().toISOString()
  },
  {
    id: 'demo-3',
    scan_id: 'demo-scan',
    module_id: 'privacy_log',
    file_path: 'src/utils/logger.js',
    line: 23,
    issue_type: 'pii_in_logs',
    severity: 'medium',
    message: 'Personal information detected in log statements',
    recommendation: 'Sanitize logs to remove PII before logging',
    is_premium: false,
    description: 'Finds PII in logs and console outputs',
    user_value: 'Protects user privacy and ensures GDPR compliance',
    engine: 'regex',
    type: 'privacy',
    created_at: new Date().toISOString()
  },
  {
    id: 'demo-4',
    scan_id: 'demo-scan',
    module_id: 'rls_validator',
    file_path: 'supabase/migrations/001_initial.sql',
    line: 15,
    issue_type: 'missing_rls_policy',
    severity: 'high',
    message: 'Table created without Row Level Security enabled',
    recommendation: 'Enable RLS and create appropriate policies for user data isolation',
    is_premium: true,
    description: 'Validates Row Level Security policies',
    user_value: 'Ensures proper data isolation between users',
    engine: 'regex',
    type: 'security',
    created_at: new Date().toISOString()
  },
  {
    id: 'demo-5',
    scan_id: 'demo-scan',
    module_id: 'netlify_headers',
    file_path: 'netlify.toml',
    line: 0,
    issue_type: 'missing_security_headers',
    severity: 'medium',
    message: 'Missing Content-Security-Policy header in Netlify configuration',
    recommendation: 'Add comprehensive security headers to protect against XSS and other attacks',
    is_premium: false,
    description: 'Checks for missing security headers',
    user_value: 'Protects users from common web vulnerabilities',
    engine: 'regex',
    type: 'security',
    created_at: new Date().toISOString()
  },
];

// Calculate category scores based on findings
const calculateCategoryScores = (findings: ScanFinding[]): CategoryScores => {
  const grouped = groupFindingsByCategory(findings);
  
  const calculateScore = (categoryFindings: ScanFinding[]): number => {
    if (categoryFindings.length === 0) return 100;
    
    const severityWeights = {
      critical: 5,
      high: 4,
      medium: 3,
      low: 2,
      info: 1
    };
    
    const totalDeduction = categoryFindings.reduce((sum, finding) => {
      return sum + (severityWeights[finding.severity] || 2);
    }, 0);
    
    return Math.max(0, 100 - totalDeduction);
  };

  const security = calculateScore(grouped.security);
  const privacy = calculateScore(grouped.privacy);
  const compliance = calculateScore(grouped.compliance);
  const overall = Math.round((security + privacy + compliance) / 3);

  return { security, privacy, compliance, overall };
};

// Group findings by category
const groupFindingsByCategory = (findings: ScanFinding[]): GroupedFindings => {
  const grouped: GroupedFindings = {
    security: [],
    privacy: [],
    compliance: [],
    miscellaneous: []
  };

  findings.forEach(finding => {
    // First try to map by module_id, then fall back to finding.type, then miscellaneous
    let category = SCANNER_CATEGORY_MAP[finding.module_id];
    
    if (!category) {
      // Fallback to the finding's type field if module_id mapping is not found
      if (['security', 'privacy', 'compliance'].includes(finding.type)) {
        category = finding.type as 'security' | 'privacy' | 'compliance';
      } else if (finding.type === 'legal') {
        category = 'compliance'; // Map 'legal' type to compliance category
      } else {
        category = 'miscellaneous'; // seo, design, scalability, etc.
      }
    }
    
    grouped[category].push(finding);
  });

  return grouped;
};

// Demo mode simulation with progressive loading
const runDemoScanSimulation = (
  onProgress: (progress: ScanProgress) => void,
  onComplete: (findings: ScanFinding[]) => void
) => {
  let currentScannerIndex = 0;
  let completedScanners: string[] = [];
  const totalDuration = DEMO_SCANNERS.reduce((sum, scanner) => sum + scanner.duration, 0);
  let elapsedTime = 0;

  console.log('[useScanResults] 🎯 Starting demo scan simulation');

  // Initial state
  onProgress({
    stage: 'initializing',
    progress: 0,
    completedScanners: [],
    totalScanners: DEMO_SCANNERS.length,
    message: 'Initializing security scan...'
  });

  const runNextScanner = () => {
    if (currentScannerIndex >= DEMO_SCANNERS.length) {
      // Complete the scan
      onProgress({
        stage: 'completed',
        progress: 100,
        completedScanners,
        totalScanners: DEMO_SCANNERS.length,
        message: 'Scan completed successfully!'
      });
      
      setTimeout(() => {
        onComplete(createDemoFindings());
      }, 200);
      return;
    }

    const scanner = DEMO_SCANNERS[currentScannerIndex];
    
    onProgress({
      stage: 'scanning',
      progress: Math.round((elapsedTime / totalDuration) * 90), // Leave 10% for analysis
      currentScanner: scanner.name,
      completedScanners: [...completedScanners],
      totalScanners: DEMO_SCANNERS.length,
      message: `Running ${scanner.name}...`
    });

    setTimeout(() => {
      completedScanners.push(scanner.id);
      elapsedTime += scanner.duration;
      currentScannerIndex++;
      
      // Add short analysis phase
      if (currentScannerIndex >= DEMO_SCANNERS.length) {
        onProgress({
          stage: 'analyzing',
          progress: 95,
          completedScanners,
          totalScanners: DEMO_SCANNERS.length,
          message: 'Analyzing results and generating report...'
        });
        
        setTimeout(runNextScanner, 300);
      } else {
        runNextScanner();
      }
    }, scanner.duration);
  };

  setTimeout(runNextScanner, 500);
};

export function useScanResults(mode: 'real' | 'demo', scanId?: string) {
  const [scanData, setScanData] = useState<ScanData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<ScanProgress | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsAuthenticated(!!session?.user);
    });
  }, []);

  const runDemoScan = useCallback(() => {
    if (isAuthenticated) {
      // Authenticated users get the simulated demo scan (legacy)
      console.log('[useScanResults] 🎮 Starting demo mode');
      setLoading(true);
      setError(null);
      setScanData(null);
      
      runDemoScanSimulation(
        (progressUpdate) => {
          setProgress(progressUpdate);
        },
        (findings) => {
          setScanData({
            id: 'demo-scan',
            status: 'completed',
            created_at: new Date().toISOString(),
            source_type: 'demo',
            findings,
            metadata: {
              file_size: 1024 * 50, // 50KB
              scan_duration: 2500,
              files_scanned: 12,
              extracted_files: [
                'src/auth.js',
                'src/database.js', 
                'src/logger.js',
                'src/config.js',
                'src/utils.js',
                'package.json',
                'package-lock.json',
                'README.md',
                '.env.example',
                'netlify.toml',
                '.gitignore',
                'src/components/Header.js',
                'src/components/Footer.js',
                'src/styles/main.css',
                'public/index.html'
              ] // Complete file list including files without findings
            }
          });
          setLoading(false);
          setProgress(null);
        }
      );
    } else {
      // Unauthenticated users: fetch from public_demo_scan_results
      setLoading(true);
      setError(null);
      setScanData(null);
      getDemoScanResults()
        .then(result => {
          setScanData({
            id: result.scan.id,
            status: 'completed',
            created_at: new Date().toISOString(),
            source_type: 'demo',
            findings: result.findings,
            metadata: result.scan.metadata
          });
        })
        .catch(err => {
          setError(err instanceof Error ? err.message : 'Failed to load demo scan results');
        })
        .finally(() => setLoading(false));
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (mode === 'demo') {
      runDemoScan();
    } else if (mode === 'real' && scanId) {
      fetchRealScanResults(scanId);
    }
  }, [mode, scanId, runDemoScan]);

  const fetchRealScanResults = async (scanId: string) => {
    try {
      setLoading(true);
      setError(null);
      setProgress({
        stage: 'initializing',
        progress: 0,
        completedScanners: [],
        totalScanners: 0,
        message: 'Loading scan results...'
      });

      console.log('[useScanResults] 📡 Fetching real scan results for:', scanId);

      setProgress({
        stage: 'scanning',
        progress: 30,
        completedScanners: [],
        totalScanners: 0,
        message: 'Fetching scan findings...'
      });

      // Use the API endpoint instead of direct Supabase queries
      const result = await getScanResults(scanId);

      if (result.status === 'error') {
        throw new Error(result.error || 'Failed to fetch scan results');
      }

      if (!result.scan || !result.findings) {
        throw new Error('Invalid response from scan results API');
      }

      setProgress({
        stage: 'analyzing',
        progress: 80,
        completedScanners: [],
        totalScanners: 0,
        message: 'Processing results...'
      });

      // Small delay for better UX
      await new Promise(resolve => setTimeout(resolve, 300));

      setScanData({
        id: result.scan.id,
        status: result.scan.status,
        created_at: result.scan.created_at,
        source_type: result.scan.source_type,
        findings: result.findings,
        metadata: result.scan.metadata
      });

      console.log('[useScanResults] ✅ Loaded', result.findings.length, 'findings');
      if (result.premiumFindings && result.premiumFindings > 0) {
        console.log('[useScanResults] 🔒', result.premiumFindings, 'premium findings hidden');
      }
    } catch (err) {
      console.error('[useScanResults] ❌ Error fetching scan results:', err);
      setError(err instanceof Error ? err.message : 'Failed to load scan results');
    } finally {
      setLoading(false);
      setProgress(null);
    }
  };

  // Computed values
  const groupedFindings = scanData ? groupFindingsByCategory(scanData.findings) : null;
  const categoryScores = scanData ? calculateCategoryScores(scanData.findings) : null;

  return {
    scanData,
    groupedFindings,
    categoryScores,
    loading,
    error,
    progress,
    refetch: () => {
      if (mode === 'real' && scanId) {
        fetchRealScanResults(scanId);
      } else if (mode === 'demo') {
        runDemoScan();
      }
    }
  };
} 